import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ListChecks, Navigation, Clock, FileDown } from "lucide-react";
import { TrackingMap } from "@/components/TrackingMap";
import { STATUS_META, today } from "@/lib/constants";

export default function MesCourses() {
  const [rides, setRides] = useState([]);
  const [active, setActive] = useState(null);
  const [tracking, setTracking] = useState(null);
  const timer = useRef(null);

  useEffect(() => { api.get("/patient/rides").then((r) => setRides(r.data)); }, []);

  const openTracking = (ride) => {
    setActive(ride);
    const poll = async () => {
      try { const { data } = await api.get(`/rides/${ride.id}/tracking`); setTracking(data); }
      catch (e) { console.error("Suivi course: échec du polling", e); }
    };
    poll();
    timer.current = setInterval(poll, 4000);
  };

  const closeTracking = () => {
    setActive(null); setTracking(null);
    if (timer.current) clearInterval(timer.current);
  };

  useEffect(() => () => timer.current && clearInterval(timer.current), []);

  const receipt = async (r) => {
    try {
      const res = await api.get(`/rides/${r.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `recu_pacemia_${r.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error("Reçu PDF: échec du téléchargement", e); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <ListChecks className="h-6 w-6 text-primary" /> Mes courses
        </h1>
        <p className="text-muted-foreground text-sm">Suivez en temps réel l'approche de votre véhicule.</p>
      </div>

      {rides.length === 0 && <Card className="p-8 rounded-md shadow-none text-center text-muted-foreground">Aucune course. Réservez votre premier trajet !</Card>}

      <div className="space-y-3" data-testid="patient-rides">
        {rides.map((r) => (
          <Card key={r.id} className="p-4 rounded-md shadow-none" data-testid={`patient-ride-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{new Date(r.date).toLocaleDateString("fr-FR")} · {r.time}</p>
                  <Badge className={STATUS_META[r.status]?.cls}>{STATUS_META[r.status]?.label}</Badge>
                  <Badge variant="outline" className="capitalize">{r.type}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.pickup_address} → {r.dropoff_address}</p>
                <p className="text-xs text-muted-foreground">{r.company_name} · {r.cost} €</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="gap-2" onClick={() => receipt(r)} data-testid={`receipt-${r.id}`}>
                  <FileDown className="h-4 w-4" /> Reçu PDF
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => openTracking(r)} data-testid={`track-${r.id}`}>
                  <Navigation className="h-4 w-4" /> Suivre
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && closeTracking()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Suivi en temps réel</DialogTitle></DialogHeader>
          {tracking && (
            <div className="space-y-3" data-testid="tracking-panel">
              <div className="flex items-center justify-between">
                <Badge className={STATUS_META[tracking.status]?.cls}>{STATUS_META[tracking.status]?.label}</Badge>
                {tracking.eta_min != null && (
                  <span className="text-sm flex items-center gap-1 font-medium"><Clock className="h-4 w-4 text-primary" /> Arrivée dans ~{tracking.eta_min} min</span>
                )}
              </div>
              <TrackingMap location={tracking.driver_location} />
              {!tracking.driver_location && <p className="text-xs text-muted-foreground text-center">Le véhicule n'a pas encore démarré. La position s'affichera dès le départ du chauffeur.</p>}
              <div className="text-sm text-muted-foreground">
                <p>{tracking.vehicle_label || "Véhicule en cours d'affectation"}</p>
                {tracking.driver_names?.length > 0 && <p>Équipage : {tracking.driver_names.join(", ")}</p>}
                <p className="mt-1">{tracking.pickup_address} → {tracking.dropoff_address}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
